// ================================================================================================
//               CLASSIFICADOR DE HERDEIROS E PROPRIEDADES SUCESSÓRIAS (VERSÃO AJUSTADA)
// ================================================================================================

import { Inventario, PessoaInventario } from '../types';
import { getEffectiveFiguraSucessoria } from './figuraSucessoria';

// Interface de saída, conforme solicitado
export interface SuccessoryProps {
  id: string;
  vinculoDC: 'Descendente' | 'Ascendente' | 'Colateral' | 'Cônjuge / Companheiro' | 'De Cujus' | 'Outro';
  classeSucessoria: 0 | 1 | 2 | 3 | 4 | 5; 
  ehHerdeiroLegitimo: boolean;
  ehHerdeiroNecessario: boolean;
  ehSucessorPorRepresentacao: boolean;
  grauParentesco: number;
  linhaParentesco: 'Reta' | 'Colateral' | 'Afinidade' | 'Nenhuma';
  isBilateral?: boolean;
}

type FamilyMap = Map<string, PessoaInventario>;

/**
 * Classifica todos os membros da família de acordo com as regras de sucessão do Código Civil brasileiro.
 * @param decujusId - O ID da pessoa falecida (De Cujus).
 * @param allPeople - Um array com todas as pessoas na árvore do inventário.
 * @param familyMap - Um mapa de ID para objeto PessoaInventario para acesso rápido.
 * @param inventario - Opcional. Quando informado, a figura sucessória é obtida das seções (Herdeiros Testamentários, Legados, Doações).
 * @returns Um Map onde a chave é o ID da pessoa e o valor é um objeto SuccessoryProps com os dados legais.
 */
export function HeirClassifier(
    decujusId: string, 
    allPeople: PessoaInventario[], 
    familyMap: FamilyMap,
    inventario?: Inventario
): Map<string, SuccessoryProps> {

    const results = new Map<string, SuccessoryProps>();
    const decujus = familyMap.get(decujusId);

    if (!decujus) {
        return results;
    }

    // --- FUNÇÕES AUXILIARES DE TRAVERSAL (BFS) ---
    const findPathToAncestor = (startId: string, targetAncestorId: string): number => {
        if (startId.toString() === targetAncestorId.toString()) return 0;
        const queue: { id: string; path: string[] }[] = [{ id: startId, path: [startId] }];
        const visited = new Set([startId]);

        while (queue.length > 0) {
            const { id: currentId, path } = queue.shift()!;
            const currentPerson = familyMap.get(currentId);

            if (currentPerson?.parentIds) {
                for (const parentId of currentPerson.parentIds) {
                    if (parentId.toString() === targetAncestorId.toString()) {
                        return path.length;
                    }
                    if (!visited.has(parentId)) {
                        visited.add(parentId);
                        queue.push({ id: parentId, path: [...path, parentId] });
                    }
                }
            }
        }
        return -1; // Não encontrado
    };

    // --- ETAPA 1: CLASSIFICAÇÃO INICIAL BASEADA EM PARENTESCO E TIPO PRÓPRIO ---
    const conjugeId = decujus.spouseIds?.[0];

    allPeople.forEach(person => {
        let props: SuccessoryProps = {
            id: person.id,
            vinculoDC: 'Outro',
            classeSucessoria: 5,
            ehHerdeiroLegitimo: false,
            ehHerdeiroNecessario: false,
            ehSucessorPorRepresentacao: false,
            grauParentesco: -1,
            linhaParentesco: 'Nenhuma',
        };
        results.set(person.id, props);

        if (person.id.toString() === decujusId.toString()) {
            props.vinculoDC = 'De Cujus';
            props.classeSucessoria = 0;
            return; 
        }
        
        // <<< FIGURA SUCESSÓRIA: Legatário / Herdeiro Testamentário (excluídos da legítima) >>>
        // Donatário NÃO é excluído: herdeiro necessário que recebeu doação continua herdeiro; a doação é colacionada (Art. 2.003 CC).
        const effectiveTipo = inventario
            ? getEffectiveFiguraSucessoria(person.id.toString(), inventario).tipo
            : person.tipo_herdeiro;
        const nonLegitimateTypes = ['Legatário', 'Herdeiro Testamentário'];
        if (effectiveTipo && nonLegitimateTypes.includes(effectiveTipo)) {
            // Apenas identificamos o vínculo para fins de exibição; 'ehHerdeiroLegitimo' permanece 'false'.
            const grauDescendenteCheck = findPathToAncestor(person.id, decujusId);
            if (grauDescendenteCheck > 0) {
                props.vinculoDC = 'Descendente';
            }
            return;
        }

        if (conjugeId && person.id.toString() === conjugeId.toString()) {
            props.vinculoDC = 'Cônjuge / Companheiro';
            props.ehHerdeiroNecessario = true;
            props.ehHerdeiroLegitimo = true;
            props.grauParentesco = 0;
            props.linhaParentesco = 'Afinidade';
            return;
        }

        const grauDescendente = findPathToAncestor(person.id, decujusId);
        if (grauDescendente > 0) {
            props.vinculoDC = 'Descendente';
            props.linhaParentesco = 'Reta';
            props.grauParentesco = grauDescendente;
            props.ehHerdeiroNecessario = true;
            props.ehHerdeiroLegitimo = true;
            // Art. 1.852 CC: direito de representação na linha reta descendente — se o parente na linha (mais próximo do de cujus) estiver morto, este descendente herda por representação
            if (grauDescendente > 1 && person.parentIds?.length) {
                const parentInLineId = person.parentIds.find(pid => findPathToAncestor(decujusId, pid) === grauDescendente - 1);
                const parentInLine = parentInLineId ? familyMap.get(parentInLineId) : null;
                if (parentInLine && !parentInLine.esta_vivo) {
                    props.ehSucessorPorRepresentacao = true;
                }
            }
            return;
        }

        const grauAscendente = findPathToAncestor(decujusId, person.id);
        if (grauAscendente > 0) {
            props.vinculoDC = 'Ascendente';
            props.linhaParentesco = 'Reta';
            props.grauParentesco = grauAscendente;
            props.ehHerdeiroNecessario = true;
            props.ehHerdeiroLegitimo = true;
            return;
        }

        let commonAncestorId: string | null = null;
        let grauDcToAncestor = -1;
        let grauPersonToAncestor = -1;
        const dcAncestorsQueue = [...(decujus.parentIds || [])];
        const visited = new Set(dcAncestorsQueue);
        
        while(dcAncestorsQueue.length > 0 && !commonAncestorId) {
            const currentAncestorId = dcAncestorsQueue.shift()!;
            const pathFromPerson = findPathToAncestor(person.id, currentAncestorId);
            if (pathFromPerson !== -1) {
                commonAncestorId = currentAncestorId;
                grauPersonToAncestor = pathFromPerson;
                grauDcToAncestor = findPathToAncestor(decujusId, currentAncestorId);
            } else {
                const ancestor = familyMap.get(currentAncestorId);
                ancestor?.parentIds?.forEach(pid => {
                    if (!visited.has(pid)) {
                        visited.add(pid);
                        dcAncestorsQueue.push(pid);
                    }
                });
            }
        }

        if (commonAncestorId) {
            const grau = grauDcToAncestor + grauPersonToAncestor;
            if (grau <= 4) {
                props.vinculoDC = 'Colateral';
                props.linhaParentesco = 'Colateral';
                props.grauParentesco = grau;
                props.ehHerdeiroLegitimo = true;
                if (grau === 2) { // Irmãos
                    const dcParents = new Set(decujus.parentIds);
                    const personParents = new Set(person.parentIds);
                    if (dcParents.size > 0 && personParents.size > 0) {
                        const commonParents = [...Array.from(dcParents)].filter(id => personParents.has(id));
                        props.isBilateral = commonParents.length === 2;
                    }
                }
                // ehSucessorPorRepresentacao para sobrinhos será definido na etapa seguinte (após todos classificados)
            }
        }
    });

    // Sobrinhos (grau 3, filhos de irmãos): marcar representação se o pai irmão estiver morto — Art. 1.853 CC (após todos terem vinculo/grau definidos)
    for (const [personId, props] of Array.from(results.entries())) {
        if (props.vinculoDC === 'Colateral' && props.grauParentesco === 3) {
            const person = familyMap.get(personId);
            const parentId = person?.parentIds?.find(pid => {
                const parentProps = results.get(pid);
                return parentProps?.vinculoDC === 'Colateral' && parentProps?.grauParentesco === 2;
            });
            const parent = parentId ? familyMap.get(parentId) : null;
            if (parent && !parent.esta_vivo) {
                props.ehSucessorPorRepresentacao = true;
            }
        }
    }

    // --- ETAPA 1.5: INVALIAÇÃO DE CADEIA SUCESSÓRIA QUEBRADA ---
    for (const props of Array.from(results.values())) {
        if (props.vinculoDC === 'Descendente' && props.grauParentesco > 1) {
            const person = familyMap.get(props.id)!;
            const parentInLineId = person.parentIds?.find(pid => findPathToAncestor(pid, decujusId) === props.grauParentesco - 1);
            if (parentInLineId) {
                const parentProps = results.get(parentInLineId);
                if (parentProps && !parentProps.ehHerdeiroLegitimo) {
                    props.vinculoDC = 'Outro'; props.classeSucessoria = 5; props.ehHerdeiroLegitimo = false; props.ehHerdeiroNecessario = false; props.grauParentesco = -1; props.linhaParentesco = 'Nenhuma';
                }
            }
        }
    }
    
    const ascendants = Array.from(results.values())
        .filter(p => p.vinculoDC === 'Ascendente')
        .sort((a, b) => a.grauParentesco - b.grauParentesco);

    for (const ascendantProps of ascendants) {
        if (ascendantProps.grauParentesco > 1) {
            let childInLineId: string | undefined = undefined;
            for (const person of allPeople) {
                if (person.parentIds?.includes(ascendantProps.id)) {
                    const path = findPathToAncestor(decujusId, person.id);
                    if (path === ascendantProps.grauParentesco - 1) {
                        childInLineId = person.id;
                        break;
                    }
                }
            }
            if (childInLineId) {
                const childInLineProps = results.get(childInLineId)!;
                if (!childInLineProps.ehHerdeiroLegitimo) {
                    ascendantProps.vinculoDC = 'Outro'; ascendantProps.classeSucessoria = 5; ascendantProps.ehHerdeiroLegitimo = false; ascendantProps.ehHerdeiroNecessario = false; ascendantProps.grauParentesco = -1; ascendantProps.linhaParentesco = 'Nenhuma';
                }
            }
        }
    }
    
    // --- ETAPA 2: CLASSIFICAÇÃO SUCESSÓRIA (ORDEM DE VOCAÇÃO) ---
    const getLivingHeirsOfVinculo = (vinculo: string) => 
        Array.from(results.values()).filter(p => p.vinculoDC === vinculo && familyMap.get(p.id)?.esta_vivo && p.ehHerdeiroLegitimo);

    const descendentesVivos = getLivingHeirsOfVinculo('Descendente');
    const ascendentesVivos = getLivingHeirsOfVinculo('Ascendente');
    const conjugeIdV = decujus.spouseIds?.[0];
    const conjugeVivoProps = conjugeIdV ? results.get(conjugeIdV) : null;
    const isConjugeVivoEHeredeiro = conjugeVivoProps?.ehHerdeiroLegitimo && familyMap.get(conjugeIdV!)?.esta_vivo;

    if (descendentesVivos.length > 0) {
        const minGrau = Math.min(...descendentesVivos.map(d => d.grauParentesco));
        descendentesVivos.forEach(d => {
            // Apenas herdeiros de grau mais próximo herdam, ou seus representantes.
            const isRepresentante = d.grauParentesco > minGrau && d.ehSucessorPorRepresentacao;
            if (d.grauParentesco === minGrau || isRepresentante) {
                results.get(d.id)!.classeSucessoria = 1;
            }
        });
        if (isConjugeVivoEHeredeiro) {
            results.get(conjugeIdV!)!.classeSucessoria = 1;
        }
    } else if (ascendentesVivos.length > 0) {
        const minGrau = Math.min(...ascendentesVivos.map(a => a.grauParentesco));
        ascendentesVivos.forEach(a => {
            if (a.grauParentesco === minGrau) {
                 results.get(a.id)!.classeSucessoria = 2;
            }
        });
        if (isConjugeVivoEHeredeiro) {
            results.get(conjugeIdV!)!.classeSucessoria = 2;
        }
    } else if (isConjugeVivoEHeredeiro) {
        results.get(conjugeIdV!)!.classeSucessoria = 3;
    } else {
        const colateraisVivos = getLivingHeirsOfVinculo('Colateral');
        if (colateraisVivos.length > 0) {
            const minGrau = Math.min(...colateraisVivos.map(c => c.grauParentesco));
            colateraisVivos.forEach(c => {
                const isRepresentante = c.grauParentesco > minGrau && c.ehSucessorPorRepresentacao;
                if (c.grauParentesco === minGrau || isRepresentante) {
                    results.get(c.id)!.classeSucessoria = 4;
                }
            });
        }
    }

    return results;
}

